import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  ListGroup,
  InputGroup,
  Form
} from 'react-bootstrap';
import {
  FaChartBar,
  FaDatabase,
  FaSearch,
  FaArrowRight,
  FaUsers,
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaCalendar,
  FaTag,
  FaFileAlt,
  FaChartLine,
  FaShoppingCart,
  FaGraduationCap,
  FaBook,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaSchool,
  FaMoneyBillWave,
  FaTasks,
  FaProjectDiagram
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import './css/ReportBuilder.css';

const ReportBuilder = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [objects, setObjects] = useState([]);
  const [relatedLists, setRelatedLists] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableObjects, setAvailableObjects] = useState([]);
  const [lookupFields, setLookupFields] = useState([]);

  // Handle cancel button click
  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      // Navigate to reports list page
      navigate('/reports');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Use the new ReportBuilder API endpoint
      const reportBuilderApi = new DataApi('reportbuilder/report-types');
      const response = await reportBuilderApi.fetchAll();

      if (response?.data?.success) {
        const reportTypes = response.data.data;
        console.log('Loaded report types:', reportTypes);

        // Debug: Check if target_field_name is present in the response
        const relationshipTypes = reportTypes.filter(type => type.type === 'relationship');
        console.log('Relationship types from API:', relationshipTypes);
        relationshipTypes.forEach(type => {
          console.log(`Type: ${type.source_object_name} -> ${type.target_object_name}`);
          console.log(`Source field: ${type.source_field_name}`);
          console.log(`Target field: ${type.target_field_name}`);
        });

        // Check for duplicate IDs
        const ids = reportTypes.map(type => type.id);
        const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
        if (duplicateIds.length > 0) {
          console.warn('Duplicate IDs found:', duplicateIds);
          console.warn('Report types with duplicate IDs:', reportTypes.filter(type => duplicateIds.includes(type.id)));
        }

        setReportTypes(reportTypes);

        // Extract objects and related lists from report types
        const objects = reportTypes
          .filter(type => type.type === 'individual')
          .map(type => ({
            object_name: type.object_name,
            object_label: type.object_label,
            allow_activity: type.allow_activity,
            is_standard: type.is_standard
          }));

        const relatedLists = reportTypes
          .filter(type => type.type === 'relationship')
          .map(type => ({
            source_object_name: type.source_object_name,
            target_object_name: type.target_object_name,
            relationship_name: type.relationship_name,
            relationship_label: type.relationship_label,
            relationship_type: type.relationship_type,
            fields: type.fields,
            actions: type.actions
          }));

        setObjects(objects);
        setRelatedLists(relatedLists);
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };


  const getObjectIcon = (objectName) => {
    if (!objectName) return FaDatabase;

    const iconMap = {
      'contact': FaUser,
      'account': FaBuilding,
      'lead': FaUser,
      'student': FaUserGraduate,
      'class': FaChalkboardTeacher,
      'department': FaSchool,
      'employee': FaUsers,
      'company': FaBuilding,
      'business': FaBuilding,
      'campaign': FaChartLine,
      'product': FaShoppingCart,
      'pricebook': FaMoneyBillWave,
      'task': FaTasks,
      'incident': FaFileAlt,
      'message': FaEnvelope,
      'notification': FaEnvelope,
      'workflow': FaProjectDiagram,
      'report': FaChartLine,
      'dashboard': FaChartLine,
      'fee': FaMoneyBillWave,
      'payment': FaMoneyBillWave,
      'file': FaFileAlt,
      'tags': FaTag,
      'recordtype': FaTag,
      'role': FaUsers,
      'user': FaUser,
      'group': FaUsers
    };

    // Try exact match first
    if (iconMap[objectName.toLowerCase()]) {
      return iconMap[objectName.toLowerCase()];
    }

    // Try partial matches
    const lowerName = objectName.toLowerCase();
    if (lowerName.includes('contact')) return FaUser;
    if (lowerName.includes('account')) return FaBuilding;
    if (lowerName.includes('lead')) return FaUser;
    if (lowerName.includes('student')) return FaUserGraduate;
    if (lowerName.includes('class')) return FaChalkboardTeacher;
    if (lowerName.includes('department')) return FaSchool;
    if (lowerName.includes('employee')) return FaUsers;
    if (lowerName.includes('company') || lowerName.includes('business')) return FaBuilding;
    if (lowerName.includes('campaign')) return FaChartLine;
    if (lowerName.includes('product')) return FaShoppingCart;
    if (lowerName.includes('price') || lowerName.includes('fee') || lowerName.includes('payment')) return FaMoneyBillWave;
    if (lowerName.includes('task')) return FaTasks;
    if (lowerName.includes('message') || lowerName.includes('notification')) return FaEnvelope;
    if (lowerName.includes('workflow')) return FaProjectDiagram;
    if (lowerName.includes('report') || lowerName.includes('dashboard')) return FaChartLine;
    if (lowerName.includes('file')) return FaFileAlt;
    if (lowerName.includes('tag') || lowerName.includes('recordtype')) return FaTag;
    if (lowerName.includes('role') || lowerName.includes('group') || lowerName.includes('user')) return FaUsers;

    return FaDatabase; // Default icon
  };

  const getObjectColor = (objectName) => {
    if (!objectName) return '#6c757d';

    const colorMap = {
      'contact': '#007bff',
      'account': '#28a745',
      'lead': '#ffc107',
      'student': '#17a2b8',
      'class': '#6f42c1',
      'department': '#fd7e14',
      'employee': '#20c997',
      'company': '#6c757d',
      'business': '#6c757d',
      'campaign': '#e83e8c',
      'product': '#dc3545',
      'pricebook': '#fd7e14',
      'task': '#6f42c1',
      'incident': '#dc3545',
      'message': '#17a2b8',
      'notification': '#17a2b8',
      'workflow': '#6f42c1',
      'report': '#28a745',
      'dashboard': '#28a745',
      'fee': '#fd7e14',
      'payment': '#28a745',
      'file': '#6c757d',
      'tags': '#ffc107',
      'recordtype': '#ffc107',
      'role': '#17a2b8',
      'user': '#007bff',
      'group': '#17a2b8'
    };

    // Try exact match first
    if (colorMap[objectName.toLowerCase()]) {
      return colorMap[objectName.toLowerCase()];
    }

    // Try partial matches
    const lowerName = objectName.toLowerCase();
    if (lowerName.includes('contact')) return '#007bff';
    if (lowerName.includes('account')) return '#28a745';
    if (lowerName.includes('lead')) return '#ffc107';
    if (lowerName.includes('student')) return '#17a2b8';
    if (lowerName.includes('class')) return '#6f42c1';
    if (lowerName.includes('department')) return '#fd7e14';
    if (lowerName.includes('employee')) return '#20c997';
    if (lowerName.includes('company') || lowerName.includes('business')) return '#6c757d';
    if (lowerName.includes('campaign')) return '#e83e8c';
    if (lowerName.includes('product')) return '#dc3545';
    if (lowerName.includes('price') || lowerName.includes('fee') || lowerName.includes('payment')) return '#fd7e14';
    if (lowerName.includes('task')) return '#6f42c1';
    if (lowerName.includes('message') || lowerName.includes('notification')) return '#17a2b8';
    if (lowerName.includes('workflow')) return '#6f42c1';
    if (lowerName.includes('report') || lowerName.includes('dashboard')) return '#28a745';
    if (lowerName.includes('file')) return '#6c757d';
    if (lowerName.includes('tag') || lowerName.includes('recordtype')) return '#ffc107';
    if (lowerName.includes('role') || lowerName.includes('group') || lowerName.includes('user')) return '#17a2b8';

    return '#6c757d'; // Default color
  };

  const loadAvailableObjects = async (reportType) => {
    try {
      const primaryObjectName = reportType.type === 'individual'
        ? reportType.object_name
        : reportType.source_object_name;

      // Load fields for the primary object
      const api = new DataApi(`reportbuilder/objects/${primaryObjectName}/fields`);
      const response = await api.fetchAll();

      if (true) {
        const fields = response.data.data;

        // Find lookup fields (fields with is_reference = true)
        const lookups = fields.filter(field => field.is_reference && field.reference_object);
        setLookupFields(lookups);

        // Get unique reference objects
        const referenceObjects = [...new Set(lookups.map(field => field.reference_object))];
        setAvailableObjects(referenceObjects);

        // Create list with only primary and target objects (exclude related objects from lookup fields for now)
        let allObjects = [
          {
            object_name: primaryObjectName,
            object_label: reportType.type === 'individual'
              ? reportType.object_label
              : reportType.source_object_label,
            is_primary: true
          }
        ];

        // For relationship types, include the target object
        if (reportType.type === 'relationship' && reportType.target_object_name) {
          allObjects.push({
            object_name: reportType.target_object_name,
            object_label: reportType.target_object_label || reportType.target_object_name.charAt(0).toUpperCase() + reportType.target_object_name.slice(1),
            is_primary: false
          });
        }

        // Store all objects in the reportType for later use
        reportType.allObjects = allObjects;
        reportType.primaryObject = primaryObjectName;
        reportType.relatedObjects = referenceObjects;
        reportType.lookupFields = lookups;

        console.log('All objects for report:', allObjects);
        console.log('Primary object:', primaryObjectName);
        console.log('Related objects:', referenceObjects);
        console.log('Lookup fields:', lookups);

        return allObjects;
      }
    } catch (error) {
      console.error('Error loading available objects:', error);
      return [];
    }
  };

  const handleReportTypeSelection = async (reportType) => {
    console.log('Selected report type:', reportType);

    // Validate the reportType object
    if (!reportType || typeof reportType !== 'object') {
      console.error('Invalid report type object:', reportType);
      return;
    }

    // Create a clean copy of the reportType to avoid any circular references or invalid properties
    const cleanReportType = {
      id: reportType.id,
      type: reportType.type,
      object_name: reportType.object_name,
      object_label: reportType.object_label,
      source_object_name: reportType.source_object_name,
      source_object_label: reportType.source_object_label,
      target_object_name: reportType.target_object_name,
      target_object_label: reportType.target_object_label,
      relationship_name: reportType.relationship_name,
      relationship_label: reportType.relationship_label,
      relationship_type: reportType.relationship_type,
      source_field_name: reportType.source_field_name,
      target_field_name: reportType.target_field_name,
      description: reportType.description,
      is_standard: reportType.is_standard,
      allow_activity: reportType.allow_activity
    };

    try {
      setSelectedReportType(cleanReportType);

      console.log('Clean report type with field names:', cleanReportType);
      console.log('Source field name:', cleanReportType.source_field_name);
      console.log('Target field name:', cleanReportType.target_field_name);

      // Load available objects and lookup fields for this report type
      await loadAvailableObjects(cleanReportType);
    } catch (error) {
      console.error('Error setting selected report type:', error);
      console.error('Report type data:', cleanReportType);
    }
  };

  const handleNext = () => {
    if (selectedReportType) {
      // Get the list of all object names
      const allObjectNames = selectedReportType.allObjects
        ? selectedReportType.allObjects.map(obj => obj.object_name)
        : [];

      // Store the report type, object names, and relationship information
      const reportData = {
        reportType: selectedReportType,
        objectNames: allObjectNames,
        relationships: selectedReportType.relationships || [] // Include relationship information
      };

      sessionStorage.setItem('selectedReportType', JSON.stringify(reportData));
      console.log('Stored report data with object names:', reportData);

      // Navigate to the report builder page
      navigate('/reportbuilderpage');
    }
  };

  const filteredReportTypes = reportTypes.filter(type =>
    type.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading report types...</p>
      </div>
    );
  }


  return (
    <div className="report-builder-simple">
      <Container fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="report-builder-header">
              <div className="report-builder-header-content">
                <div className="report-builder-header-icon">
                  <FaChartBar />
                </div>
                <div className="report-builder-header-text">
                  <h2>Report Builder</h2>
                  <p>Select the type of report you want to create</p>
                </div>
              </div>
              <div className="report-builder-header-actions">
                <Button variant="outline-secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Error Alert */}
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          {/* Left Side - Report Type Selection */}
          <Col md={6}>
            <Card className="report-builder-selection-card">
              <Card.Header>
                <div className="d-flex align-items-center">
                  <FaDatabase className="me-2" />
                  <span>Select Report Type</span>
                </div>
              </Card.Header>
              <Card.Body>
                {/* Search */}
                <div className="report-builder-search-section mb-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search report types..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>

                {/* Report Types List */}
                <div className="report-builder-report-types-list">
                  {filteredReportTypes.map((reportType, index) => {
                    const objectName = reportType.object_name || reportType.source_object_name || 'default';
                    const IconComponent = getObjectIcon(objectName);
                    const color = getObjectColor(objectName);
                    const isSelected = selectedReportType?.id === reportType.id;

                    return (
                      <div
                        key={`${reportType.id}_${index}`}
                        className={`report-builder-report-type-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleReportTypeSelection(reportType)}
                      >
                        <div className="report-builder-report-type-icon" style={{ color: color }}>
                          <IconComponent />
                        </div>
                        <div className="report-builder-report-type-content">
                          <div className="report-builder-report-type-title">
                            {reportType.description}
                          </div>
                          <div className="report-builder-report-type-meta">
                            {reportType.type === 'individual' ? (
                              <span className="report-builder-badge bg-primary">Individual</span>
                            ) : (
                              <span className="report-builder-badge bg-success">With Relationships</span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="report-builder-selection-indicator">
                            <FaArrowRight />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {filteredReportTypes.length === 0 && (
                  <div className="text-center py-4">
                    <FaDatabase size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">No report types found</h5>
                    <p className="text-muted">
                      {searchTerm ? 'Try adjusting your search terms' : 'No report types available'}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right Side - Selection Details */}
          <Col md={6}>
            <Card className="report-builder-details-card">
              <Card.Header>
                <div className="d-flex align-items-center">
                  <FaChartBar className="me-2" />
                  <span>Report Details</span>
                </div>
              </Card.Header>
              <Card.Body>
                {selectedReportType ? (
                  <div className="report-builder-selected-details">
                    <div className="report-builder-detail-header">
                      <div className="report-builder-detail-icon" style={{ color: getObjectColor(selectedReportType.object_name || selectedReportType.source_object_name) }}>
                        {(() => {
                          const IconComponent = getObjectIcon(selectedReportType.object_name || selectedReportType.source_object_name);
                          return <IconComponent />;
                        })()}
                      </div>
                      <div className="report-builder-detail-title">
                        {selectedReportType.description}
                      </div>
                    </div>

                    <div className="report-builder-detail-info">
                      <div className="report-builder-info-item">
                        <label>Type:</label>
                        <span className="report-builder-badge bg-primary">
                          {selectedReportType.type === 'individual' ? 'Individual Report' : 'Relationship Report'}
                        </span>
                      </div>

                      {selectedReportType.type === 'individual' ? (
                        <div className="report-builder-info-item">
                          <label>Object:</label>
                          <span>{selectedReportType.object_label}</span>
                        </div>
                      ) : (
                        <>
                          <div className="report-builder-info-item">
                            <label>Primary Object:</label>
                            <span>{selectedReportType.source_object_label}</span>
                          </div>
                          <div className="report-builder-info-item">
                            <label>Related Object:</label>
                            <span>{selectedReportType.target_object_label}</span>
                          </div>
                          {selectedReportType.relationship_name && (
                            <div className="report-builder-info-item">
                              <label>Relationship:</label>
                              <span>{selectedReportType.relationship_name}</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* Available Objects Section */}
                      {availableObjects.length > 0 && (
                        <div className="report-builder-info-item">
                          <label>Available Objects:</label>
                          <div className="available-objects">
                            {availableObjects.map((objectName, index) => (
                              <span key={index} className="object-badge">
                                {objectName}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Lookup Fields Section */}
                      {lookupFields.length > 0 && (
                        <div className="report-builder-info-item">
                          <label>Lookup Fields:</label>
                          <div className="lookup-fields">
                            {lookupFields.map((field, index) => (
                              <div key={index} className="lookup-field">
                                <span className="field-name">{field.field_label}</span>
                                <span className="field-reference">→ {field.reference_object}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="report-builder-detail-description">
                      <p>
                        {selectedReportType.type === 'individual'
                          ? `This report will show data from ${selectedReportType.object_label} records only.`
                          : `This report will show data from ${selectedReportType.source_object_label} records along with related ${selectedReportType.target_object_label} information.`
                        }
                      </p>
                    </div>

                    <div className="report-builder-detail-actions">
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleNext}
                        className="w-100"
                      >
                        Continue with this Report Type
                        <FaArrowRight className="ms-2" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="report-builder-no-selection">
                    <div className="text-center py-5">
                      <FaChartBar size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">Select a Report Type</h5>
                      <p className="text-muted">
                        Choose a report type from the left panel to see details and continue
                      </p>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ReportBuilder;
